<template>
  <div class="mt-2 p-2 flex flex-col gap-2 glass-frosted rounded-3xl">
    <!-- 传感器模式 -->
    <div class="flex items-stretch gap-2">
      <div
        v-for="mode in sensorModeConfigs"
        :key="mode.value"
        class="group flex-1 p-2 min-h-21.5 rounded-2xl"
        :class="[
          motionClass,
          isModeDisabled(mode.value) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        ]"
        :style="{
          background: sensorMode === mode.value ? 'var(--color-white)' : 'var(--color-key)',
        }"
        @click="handleModeClick(mode.value)"
      >
        <div class="flex items-center gap-2">
          <h3
            class="text-l-medium whitespace-nowrap"
            :class="[
              motionColorClass,
              {
                'text-text-glass-disabled': isModeDisabled(mode.value),
                'group-hover:text-text-glass-default!':
                  !isModeDisabled(mode.value) && sensorMode !== mode.value,
              },
            ]"
            :style="
              isModeDisabled(mode.value)
                ? {}
                : sensorMode === mode.value
                  ? {
                      backgroundImage: mode.styles.activeTitle,
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                    }
                  : {
                      color: 'var(--color-black-white)',
                    }
            "
          >
            {{ mode.title }}
          </h3>
          <p
            class="text-xs-semibold font-[Barlow] whitespace-nowrap"
            :class="[
              motionColorClass,
              {
                'text-text-glass-disabled': isModeDisabled(mode.value),
                'group-hover:text-text-glass-default!':
                  !isModeDisabled(mode.value) && sensorMode !== mode.value,
              },
            ]"
            :style="
              isModeDisabled(mode.value)
                ? {}
                : {
                    color:
                      sensorMode === mode.value
                        ? 'var(--color-text-important-default)'
                        : 'var(--color-black-white)',
                  }
            "
          >
            ({{ mode.subtitle }})
          </p>
        </div>
        <div class="mt-2">
          <p
            v-for="(excerpt, index) in mode.excerpts"
            :key="index"
            class="text-s-medium"
            :class="[
              motionColorClass,
              {
                'text-text-glass-disabled': isModeDisabled(mode.value),
                'group-hover:text-text-glass-default!':
                  !isModeDisabled(mode.value) && sensorMode !== mode.value,
              },
            ]"
            :style="
              isModeDisabled(mode.value)
                ? {}
                : {
                    color:
                      sensorMode === mode.value
                        ? mode.styles.activeExcerpt
                        : 'var(--color-black-white)',
                  }
            "
          >
            {{ excerpt }}
          </p>
        </div>
      </div>
    </div>

    <!-- 按键扫描率 -->
    <div
      v-if="keyScanRateOptions.length > 0"
      class="px-4 py-3 flex items-center justify-between gap-8 bg-key rounded-2xl"
    >
      <p class="text-l-medium text-black-white">{{ $t('performance.keyScanRate') }}</p>
      <div class="flex-1 flex flex-col gap-2">
        <div class="flex w-full rounded-full overflow-hidden bg-transparent gap-0.5">
          <div
            v-for="item in keyScanRateOptions"
            :key="item.value"
            class="flex-1 h-2 cursor-pointer"
            :class="motionColorClass"
            :style="{
              background:
                keyScanRate === item.value
                  ? currentModeStyles.keyScanRate
                  : 'var(--color-neutral-light-active)',
            }"
            @click="emit('update:keyScanRate', item.value)"
          />
        </div>
        <div class="flex w-full">
          <div
            v-for="item in keyScanRateOptions"
            :key="item.value"
            class="flex-1 flex justify-center cursor-pointer"
            @click="emit('update:keyScanRate', item.value)"
          >
            <Button color="normal" size="xs" shape="corner" :active="keyScanRate === item.value">
              {{ item.label }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- 回报率 -->
    <div class="relative flex items-end h-32 bg-key rounded-2xl overflow-hidden">
      <p class="absolute top-3 left-4 text-l-medium text-black-white">
        {{ $t('performance.reportRate') }}
      </p>
      <div class="w-full flex items-end gap-0.5">
        <div
          v-for="(item, idx) in reportRateOptions"
          :key="item.value"
          class="return-rate-bar flex-1 flex items-end justify-center cursor-pointer"
          :class="[motionClass, { 'is-active': reportRate === item.value }]"
          :style="{
            height: `${getReturnRateHeight(idx)}px`,
            clipPath: getReturnRateClipPath(idx),
            '--return-rate-bg': currentModeStyles.returnRate,
            '--return-rate-hover': currentModeStyles.returnRateHover,
            '--return-rate-active': currentModeStyles.activeReturnRate,
            background:
              reportRate === item.value ? 'var(--return-rate-active)' : 'var(--return-rate-bg)',
            '-webkit-text-stroke': 'initial',
          }"
          @click="emit('update:reportRate', item.value)"
        >
          <span class="font-[Barlow] pb-2.5 text-l-semibold text-black-white">
            {{ item.label }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { Button } from '@rapoo/element'
  import type { MousePerfCapability, SensorMode, KeyScanRate, ReportRate } from '@rapoo/device-sdk'
  import {
    RATE_TIER_LABELS,
    SENSOR_MODE_PRESETS,
    getReportRateSlot,
  } from '../../composables/usePerformance'
  import { useDeviceStore, useLocaleStore } from '@/stores'

  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const props = defineProps<{
    perfConfig?: MousePerfCapability['config']
    sensorMode: SensorMode
    keyScanRate: KeyScanRate
    reportRate: ReportRate
    availableSensorModes: SensorMode[]
    /** 外部同步是否过渡。页面回读期间为 false。 */
    animate?: boolean
  }>()

  const motionClass = computed(() =>
    props.animate ? 'transition-all duration-300 ease-in-out' : '',
  )
  const motionColorClass = computed(() => (props.animate ? 'transition-colors duration-300' : ''))

  const emit = defineEmits<{
    'update:sensorMode': [value: SensorMode]
    'update:keyScanRate': [value: KeyScanRate]
    'update:reportRate': [value: ReportRate]
  }>()

  // ===================== 模式样式配置 =====================
  // 按传感器模式协议值取色（5=狂暴竞技, 4=竞技, 3=超核, 2=火力, 1=均衡, 0=办公）
  const modeStylesMap: Record<
    number,
    {
      activeTitle: string
      activeExcerpt: string
      keyScanRate: string
      returnRate: string
      returnRateHover: string
      activeReturnRate: string
    }
  > = {
    5: {
      activeTitle: 'var(--color-perf-t-3t)',
      activeExcerpt: 'var(--color-brand-red)',
      keyScanRate: 'var(--color-perf-sm-3t)',
      returnRate: 'var(--color-perf-hb-kb)',
      returnRateHover: 'var(--color-perf-hb-kbb)',
      activeReturnRate: 'var(--color-perf-hb-kba)',
    },
    4: {
      activeTitle: 'var(--color-perf-t-3t)',
      activeExcerpt: 'var(--color-brand-orange)',
      keyScanRate: 'var(--color-perf-sm-3t)',
      returnRate: 'var(--color-perf-hb-jj)',
      returnRateHover: 'var(--color-perf-hb-jjb)',
      activeReturnRate: 'var(--color-perf-hb-jja)',
    },
    3: {
      activeTitle: 'var(--color-perf-t-3t)',
      activeExcerpt: 'var(--color-brand-orange)',
      keyScanRate: 'var(--color-perf-sm-3t)',
      returnRate: 'var(--color-perf-hb-cha)',
      returnRateHover: 'var(--color-perf-hb-chab)',
      activeReturnRate: 'var(--color-perf-hb-chaa)',
    },
    2: {
      activeTitle: 'var(--color-perf-t-hl)',
      activeExcerpt: 'var(--color-purple-active)',
      keyScanRate: 'var(--color-perf-sm-hl)',
      returnRate: 'var(--color-perf-hb-hl)',
      returnRateHover: 'var(--color-perf-hb-hlb)',
      activeReturnRate: 'var(--color-perf-hb-hla)',
    },
    1: {
      activeTitle: 'var(--color-perf-t-jh)',
      activeExcerpt: 'var(--color-green-active)',
      keyScanRate: 'var(--color-perf-sm-jh)',
      returnRate: 'var(--color-perf-hb-jh)',
      returnRateHover: 'var(--color-perf-hb-jhb)',
      activeReturnRate: 'var(--color-perf-hb-jha)',
    },
    0: {
      activeTitle: 'var(--color-perf-t-bg)',
      activeExcerpt: 'var(--color-brand-blue1)',
      keyScanRate: 'var(--color-perf-sm-bg)',
      returnRate: 'var(--color-perf-hb-bg)',
      returnRateHover: 'var(--color-perf-hb-bgb)',
      activeReturnRate: 'var(--color-perf-hb-bga)',
    },
  }

  const defaultStyles = modeStylesMap[0]

  /** 传感器模式档位：manifest 声明开放哪些档位，文案取对应 preset，样式按协议值取 */
  const sensorModeConfigs = computed(() => {
    const scanRate = props.perfConfig?.sensorScanRateLabel
    return (props.perfConfig?.sensorModes ?? []).map((opt) => {
      const preset = SENSOR_MODE_PRESETS[opt.preset]
      // 只有竞技档的第一条文案带 {scanRate} 占位
      const interpolateIndex = opt.preset === 'competitive' && scanRate ? 0 : -1
      return {
        value: opt.value,
        title: t(preset.titleKey),
        subtitle: preset.subtitle,
        excerpts: preset.excerptKeys.map((key, index) =>
          index === interpolateIndex ? t(key, { scanRate }) : t(key),
        ),
        styles: modeStylesMap[opt.value] ?? defaultStyles,
      }
    })
  })

  /** 当前选中模式的样式（用于扫描率和回报率条的颜色） */
  const currentModeStyles = computed(() => modeStylesMap[props.sensorMode] ?? defaultStyles)

  /**
   * 回报率选项列表。
   * 文案按芯片槽位下标取（0=125Hz…6=8000Hz），因为不同芯片的协议值编号不同但档位含义一致。
   */
  const reportRateOptions = computed(() => {
    const feature = currentDevice.value?.features.mousePerfCfg
    return (props.perfConfig?.reportRates ?? []).map((value) => ({
      value,
      label: RATE_TIER_LABELS[getReportRateSlot(feature, value)],
    }))
  })

  /** 按键扫描率选项列表；取值本身即档位下标 */
  const keyScanRateOptions = computed(() =>
    (props.perfConfig?.keyScanRates ?? []).map((value) => ({
      value,
      label: RATE_TIER_LABELS[value],
    })),
  )

  // ===================== 模式禁用判断 =====================

  function isModeDisabled(mode: SensorMode): boolean {
    return !props.availableSensorModes.includes(mode)
  }

  function handleModeClick(mode: SensorMode) {
    if (isModeDisabled(mode)) return
    emit('update:sensorMode', mode)
  }

  // ===================== 回报率柱状图辅助 =====================

  function getReturnRateHeight(idx: number): number {
    const minHeight = 52
    const maxHeight = 108
    const count = reportRateOptions.value.length
    if (count <= 1) return maxHeight
    const step = (maxHeight - minHeight) / (count - 1)
    return Math.round(minHeight + step * idx)
  }

  function getReturnRateClipPath(idx: number): string {
    const minHeight = 52
    const maxHeight = 108
    const count = reportRateOptions.value.length
    const step = count > 1 ? (maxHeight - minHeight) / (count - 1) : 0
    const currentHeight = getReturnRateHeight(idx)
    const prevHeight = idx > 0 ? getReturnRateHeight(idx - 1) : minHeight - step
    const leftTopPercent = ((1 - prevHeight / currentHeight) * 100).toFixed(1)
    return `polygon(0 ${leftTopPercent}%, 100% 0, 100% 100%, 0 100%)`
  }
</script>

<style scoped>
  .return-rate-bar:not(.is-active):hover {
    background: var(--return-rate-hover) !important;
  }
</style>
